import { OptionType } from '@europrocurement/flexy-components';
import { FlexyForm, FormStructure } from '@europrocurement/flexy-form';
import { DossierOpenSearch, useOffreService } from '@europrocurement/l2d-domain';
import { ArticlesJsonldArticleRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { CATEGORY_TAXES } from '@europrocurement/l2d-modules';
import { Button, CircularProgress, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';

export type AddTaxFormData = {
    title: string;
    prestationId: number;
    articleId: number;
};

type AddTaxFormProps = {
    onSubmit: (data: AddTaxFormData) => void;
    onCancel: () => void;
    dossier: DossierOpenSearch;
};

type SubmittedTaxFormData = {
    title: string;
    prestationId: Array<number>;
    articleId: OptionType;
};

const AddTaxForm = function ({ onSubmit, onCancel, dossier }: AddTaxFormProps) {
    const { enqueueSnackbar } = useSnackbar();
    const { articlesApi } = useOffreService();
    const [articles, setArticles] = useState<ArticlesJsonldArticleRead[]>([]);
    const [processing, setProcessing] = useState<boolean>(false);

    useEffect(() => {
        articlesApi
            .apiArticlesGetCollection({
                xIdSociete: 12,
                catArticleId: CATEGORY_TAXES,
            })
            .then((res) => setArticles(res.data['hydra:member']))
            .catch((err) => console.log(err));
    }, [articlesApi]);

    const prestationChoices = dossier.formalites
        .filter((formalite) => formalite.prestationId.length > 0)
        .map((formalite) => ({
            value: formalite.prestationId[0],
            label: formalite.libelle,
        }));

    const handleSubmit: SubmitHandler<SubmittedTaxFormData> = async (
        formData: SubmittedTaxFormData,
    ) => {
        let prestationId;

        if (prestationChoices.length === 1) {
            prestationId = prestationChoices[0].value;
        } else if (formData?.prestationId[0]) {
            prestationId = formData?.prestationId[0];
        }

        if (prestationId) {
            setProcessing(true);
            onSubmit({
                title: formData.title,
                prestationId,
                articleId: formData?.articleId?.value as number,
            });
        } else {
            enqueueSnackbar('Formalité associée non trouvée', {
                variant: 'error',
            });
        }
    };

    const formStructure: FormStructure[] = [
        {
            type: 'text',
            name: 'title',
            inputlabel: 'Nom de la démarche',
            rules: {
                required: 'Ce champ est requis',
            },
        },
        ...(prestationChoices.length > 1
            ? [
                  {
                      type: 'selectItems',
                      name: 'prestationId',
                      mode: 'inline',
                      inputlabel: 'Formalité associée',
                      colInline: 6,
                      items: prestationChoices,
                      multiple: false,
                      rules: {
                          required: 'Ce champ est requis',
                      },
                  } as FormStructure,
              ]
            : []),
        {
            type: 'select',
            name: 'articleId',
            inputlabel: 'Article',
            options: articles.map((article) => ({
                value: article.id,
                label: article.libelle || 'article sans libelle',
            })),
            rules: {
                required: 'Ce champ est requis',
            },
        },
    ];

    const formContext = useForm<FieldValues>({
        mode: 'onChange',
    });

    const formProps = {
        formContext,
        onSubmit: handleSubmit,
        submitButton: {
            displayed: false,
        },
    };

    const submitBtnSx = {
        backgroundColor: 'primary.dark',
        padding: '16px 24px',
        display: 'flex',
        alignItems: 'center',
        gap: '0 10px',
    };

    return (
        <Box>
            <Typography variant="l2dh6">Ajouter un enregistrement</Typography>
            <Box
                sx={{
                    padding: '26px 0',
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '10px',
                }}
            >
                <FlexyForm
                    formStructure={formStructure}
                    {...formProps}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    columnGap: '16px',
                }}
            >
                <Button
                    variant="text"
                    sx={{
                        padding: '16px 24px',
                        alignItems: 'center',
                        color: 'primary.dark',
                    }}
                    onClick={onCancel}
                    disabled={processing}
                >
                    Annuler
                </Button>
                <Button
                    variant="contained"
                    sx={submitBtnSx}
                    onClick={formContext.handleSubmit(handleSubmit)}
                    disabled={processing}
                >
                    {processing && (
                        <CircularProgress
                            color="inherit"
                            size="16px"
                        />
                    )}
                    {!processing && (
                        <FaOptionIcon
                            icon={['fas', 'plus']}
                            sx={{ lineHeight: 0 }}
                        />
                    )}
                    Ajouter l&apos;enregistrement
                </Button>
            </Box>
        </Box>
    );
};

export default AddTaxForm;
